import { useEffect, useState } from 'react';
import { Dropdown, Offcanvas } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '../../assets/MOOVICAR_logo_assets/15_MOOVICAR_logo_principal_transparent.png';
import userPlaceholder from '../../assets/user.png';

import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

import GuestNavlist from '../GuestNavList';
import MobileBottomNav from '../MobileBottomNav';
import NotificationBell from '../NotificationBell';

import './Header.css';

function Header() {
  const axios = useAxios();
  const location = useLocation();
  const navigate = useNavigate();

  const { user, loading, isAuthenticated } = useUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState(localStorage.getItem('mode') || 'guest');

  useEffect(() => {
    localStorage.setItem('mode', mode);
    window.dispatchEvent(new Event('mc-mode-change'));
  }, [mode]);

  const isHost = mode === 'host';

  // Le bouton indique le mode VERS lequel on bascule (pas le mode actuel)
  const switchLabel = isHost ? 'Mode locataire' : 'Mode propriétaire';

  const handleModeToggle = () => {
    const nextMode = isHost ? 'guest' : 'host';

    // Écriture immédiate : la redirection ci-dessous recharge la page et le
    // useEffect n'aurait pas forcément le temps de s'exécuter.
    localStorage.setItem('mode', nextMode);

    setMode(nextMode);
    setMenuOpen(false); // ferme le menu mobile s'il est ouvert
    window.location.assign(nextMode === 'host' ? '/account/demandes' : '/');
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');

      window.localStorage.removeItem('me');
      window.localStorage.removeItem('VehicleStorage');

      document.cookie = 'jwtToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

      setMenuOpen(false);
      window.location.replace('/');
    } catch (e) {
      console.error(e);
    }
  };

  const goTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const isAdminPage = location.pathname.includes('admin');

  const guestLinks = [
    {
      to: '/guest/listing/reservations',
      icon: 'bi-calendar-check',
      label: 'Réservations',
    },
    {
      to: '/guest/listing/reviews',
      icon: 'bi-star',
      label: 'Avis invités',
    },
    {
      to: '/guest/favorites',
      icon: 'bi-heart',
      label: 'Favoris',
    },
    {
      to: '/guest/settings',
      icon: 'bi-gear',
      label: 'Paramètres',
    },
  ];

  const hostLinks = [
    {
      to: '/account/demandes',
      icon: 'bi-inbox',
      label: 'Demandes',
    },
    {
      to: '/account/locations',
      icon: 'bi-car-front',
      label: 'Locations',
    },
    {
      to: '/account/voitures',
      icon: 'bi-key',
      label: 'Voitures',
    },
    {
      to: '/account/performances',
      icon: 'bi-bar-chart',
      label: 'Performances',
    },
    {
      to: '/business/earnings',
      icon: 'bi-wallet2',
      label: 'Revenus',
    },
    {
      to: '/account/settings',
      icon: 'bi-gear',
      label: 'Paramètres',
    },
  ];

  const navLinks = isHost ? hostLinks : guestLinks;

  if (loading) {
    return null;
  }

  return (
    <>
      {/* =====================================================
          MOBILE OFFCANVAS
      ===================================================== */}

      <Offcanvas
        show={menuOpen}
        onHide={() => setMenuOpen(false)}
        placement="end"
        className="mc-mobile-menu"
      >
        <Offcanvas.Header closeButton className="mc-mobile-menu-header">
          <Link
            to="/"
            onClick={() => {
              setMode('guest');
              setMenuOpen(false);
            }}
          >
            <img src={logo} alt="Moovicar" className="mc-mobile-logo" />
          </Link>
        </Offcanvas.Header>

        <Offcanvas.Body className="mc-mobile-menu-body">
          {/* PROFILE */}
          {isAuthenticated && user && (
            <div className="mc-mobile-profile">
              <img
                src={user.profile_url || userPlaceholder}
                alt="Profil"
                className="mc-mobile-avatar"
              />

              <div className="mc-mobile-profile-info">
                <strong>
                  {user.firstName} {user.lastName}
                </strong>

                <span>{isHost ? 'Mode propriétaire' : 'Mode locataire'}</span>
              </div>
            </div>
          )}

          {/* MODE SWITCH */}
          {isAuthenticated && (
            <button
              type="button"
              className="mc-mode-button mc-mode-button--block"
              onClick={handleModeToggle}
              aria-label={`Passer en ${switchLabel.toLowerCase()}`}
            >
              <span className="mc-mode-button-icon">
                <i className="bi bi-arrow-left-right" aria-hidden="true"></i>
              </span>

              <span className="mc-mode-button-label">{switchLabel}</span>
            </button>
          )}

          {/* NAVIGATION */}
          {isAuthenticated ? (
            <nav className="mc-mobile-navigation">
              {navLinks.map(({ to, icon, label }) => (
                <button
                  key={to}
                  type="button"
                  onClick={() => goTo(to)}
                  className="mc-mobile-nav-item"
                >
                  <span className="mc-mobile-nav-icon">
                    <i className={`bi ${icon}`}></i>
                  </span>

                  <span>{label}</span>

                  <i className="bi bi-chevron-right mc-mobile-nav-arrow"></i>
                </button>
              ))}
            </nav>
          ) : (
            <div className="mc-mobile-guest">
              <div className="mc-mobile-guest-icon">
                <i className="bi bi-person"></i>
              </div>

              <h3>Bienvenue sur Moovicar</h3>

              <p>Connectez-vous pour accéder à vos réservations, favoris et paramètres.</p>

              <button
                type="button"
                className="mc-btn mc-btn-primary"
                onClick={() => goTo('/signin')}
              >
                Se connecter
              </button>

              <button
                type="button"
                className="mc-btn mc-btn-secondary"
                onClick={() => goTo('/signup')}
              >
                Créer un compte
              </button>
            </div>
          )}

          {/* BOTTOM ACTIONS */}
          <div className="mc-mobile-actions">
            <button
              type="button"
              className="mc-mobile-rent-button"
              onClick={() => goTo('/location-voiture')}
            >
              <span>
                <i className="bi bi-car-front-fill"></i>
              </span>

              <div>
                <strong>Louer ma voiture</strong>
                <small>Gagnez de l'argent avec votre véhicule</small>
              </div>

              <i className="bi bi-arrow-right"></i>
            </button>

            {isAuthenticated && (
              <button type="button" className="mc-mobile-logout" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right"></i>
                Déconnexion
              </button>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* =====================================================
          HEADER ADMIN
      ===================================================== */}

      {isAdminPage ? (
        <header className="mc-header mc-header-admin">
          <div className="mc-header-container">
            <Link to="/" className="mc-logo" onClick={() => setMode('guest')}>
              <img src={logo} alt="Moovicar" />
            </Link>
          </div>
        </header>
      ) : (
        <header className="mc-header">
          <div className="mc-header-container">
            {/* LOGO */}
            <Link to="/" className="mc-logo" onClick={() => setMode('guest')}>
              <img src={logo} alt="Moovicar" />
            </Link>

            {/* DESKTOP ACTIONS */}
            <div className="mc-header-actions">
              <Link className="mc-header-rent-button" to="/location-voiture">
                <i className="bi bi-car-front-fill"></i>
                <span>Louer ma voiture</span>
              </Link>

              {!isAuthenticated && (
                <Link className="mc-header-login" to="/signin">
                  Se connecter
                </Link>
              )}

              {/* USER */}
              {isAuthenticated && (
                <Dropdown className="mc-user-dropdown d-none d-md-block">
                  <Dropdown.Toggle as="button" className="mc-user-button" id="user-menu">
                    <img src={user?.profile_url || userPlaceholder} alt="Profil" />

                    <span className="mc-user-chevron">
                      <i className="bi bi-chevron-down"></i>
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end" className="mc-user-menu">
                    {/* PROFILE */}
                    <div className="mc-user-menu-profile">
                      <img src={user?.profile_url || userPlaceholder} alt="Profil" />

                      <div>
                        <strong>
                          {user?.firstName} {user?.lastName}
                        </strong>

                        <span>{isHost ? 'Mode propriétaire' : 'Mode locataire'}</span>
                      </div>
                    </div>

                    {/* MODE */}
                    <div className="mc-user-mode">
                      <button
                        type="button"
                        className="mc-mode-button"
                        onClick={handleModeToggle}
                        aria-label={`Passer en ${switchLabel.toLowerCase()}`}
                      >
                        <span className="mc-mode-button-icon">
                          <i className="bi bi-arrow-left-right" aria-hidden="true"></i>
                        </span>

                        <span className="mc-mode-button-label">{switchLabel}</span>
                      </button>
                    </div>

                    {/* LINKS */}
                    <div className="mc-user-menu-links">
                      {mode === 'guest' ? (
                        <GuestNavlist />
                      ) : (
                        hostLinks.map(({ to, icon, label }) => (
                          <Dropdown.Item key={to} as={Link} to={to} className="mc-user-menu-link">
                            <span>
                              <i className={`bi ${icon}`}></i>
                            </span>

                            {label}
                          </Dropdown.Item>
                        ))
                      )}
                    </div>

                    <Dropdown.Divider />

                    {/* LOGOUT */}
                    <div className="mc-user-menu-footer">
                      <button type="button" className="mc-logout-button" onClick={handleLogout}>
                        <i className="bi bi-box-arrow-right"></i>
                        Déconnexion
                      </button>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {/* NOTIFICATIONS */}
              {isAuthenticated && (
                <div className="mc-notification-wrapper">
                  <NotificationBell />
                </div>
              )}

              {/* MOBILE */}
              <button
                type="button"
                className="mc-mobile-menu-button"
                onClick={() => setMenuOpen(true)}
                aria-label="Ouvrir le menu"
              >
                <i className="bi bi-list"></i>
              </button>
            </div>
          </div>
        </header>
      )}
      <MobileBottomNav mode={mode} userId={user?.id} />
    </>
  );
}

export default Header;
